import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarCheck } from "lucide-react";
import { useApp } from "@/state/AppContext";
import { api, apiErrorMessage, rows } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { hiMeridiem } from "@/lib/format";
import { telHref, validatePhone } from "@/lib/phone";

/** "6:00 AM" from a plain `HH:MM:SS` wall-clock string (`AttendanceSlot.start_time`/`end_time`).
 * These are NOT UTC instants — unlike `slot_start_at` elsewhere on the storefront, a batch's time
 * is stored and served as the local time-of-day it already is, so this does no timezone
 * conversion (that is what `localTime()` in `lib/format.js` is for; using it here would be
 * wrong — there is no date/offset to convert). */
function clockLabel(hhmmss, lang) {
  if (!hhmmss) return "";
  const [hStr, mStr] = hhmmss.split(":");
  let h = Number(hStr);
  const m = Number(mStr);
  if (Number.isNaN(h) || Number.isNaN(m)) return hhmmss;
  const meridiem = h >= 12 ? "PM" : "AM";
  const hour24 = h;
  h = h % 12 || 12;
  // R7-7: the SAME Hindi time-of-day formatter as every other time ("सुबह 6:00 बजे").
  return hiMeridiem(`${h}:${String(m).padStart(2, "0")} ${meridiem}`, lang, hour24);
}

/** Weekdays 0..6 (Monday..Sunday, `directory/hours.py`'s convention) -> "Mon–Sat" when the run is
 * consecutive, else a comma list — matches how the handoff writes a batch's days. */
function weekdaysLabel(weekdays, short) {
  const days = [...weekdays].sort((a, b) => a - b);
  if (days.length === 0) return "";
  const consecutive = days.every((d, i) => i === 0 || d === days[i - 1] + 1);
  if (consecutive && days.length > 2) {
    return `${short[days[0]]}–${short[days[days.length - 1]]}`;
  }
  return days.map((d) => short[d]).join(", ");
}

/** C5 (storefront S3): "Class batches" + "Book a free trial class" for an `attendance`-capable
 * business (gym, coaching centre). Reads the same batch schedule the owner's attendance desk
 * configures (`GET /businesses/:id/attendance/slots`, public/no-PII — see
 * `attendance/service.py::public_slots`). The trial capture reuses the existing enquiry pipeline
 * (no new table): when the business also has `enquiry`, "Book free trial" posts the SAME
 * `/city/businesses/:id/enquiries` capture `EnquirySection` uses, with a message that names it as
 * a trial request so it reads clearly in the owner's Requests inbox — never a bare form the owner
 * cannot tell apart from a general question. A business without `enquiry` falls back to `tel:`,
 * same degrade-through-what-the-business-has pattern `DoctorOnCall` uses.
 */
export function AttendanceSection({ business }) {
  const { t, lang } = useApp();
  const [slots, setSlots] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [chosen, setChosen] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const canCapture = (business.capabilities || []).includes("enquiry");
  const short = t("storefront.slots.weekdayShort");

  useEffect(() => {
    api
      .get(`/businesses/${business.id}/attendance/slots`)
      .then((r) => {
        const list = rows(r.data);
        setSlots(list);
        if (list[0]) setChosen(list[0]);
      })
      .finally(() => setLoaded(true));
  }, [business.id]);

  const bookTrial = async () => {
    if (!chosen) return;
    if (canCapture && !form.phone.trim()) {
      toast.error(t("enquiry.phone"));
      return;
    }
    if (canCapture) {
      // R11 PHONE: the same rule the server applies, in the visitor's language, before the round trip.
      const phoneCheck = validatePhone(form.phone, "mobile");
      if (!phoneCheck.ok) {
        toast.error(t(phoneCheck.messageKey));
        return;
      }
    }
    if (!canCapture) {
      const href = telHref(business.phone);
      if (href) window.location.href = href;
      return;
    }
    setBusy(true);
    try {
      await api.post(`/city/businesses/${business.id}/enquiries`, {
        contact_name: form.name.trim(),
        contact_phone: form.phone.trim(),
        contact_email: "",
        message: t("attendance.trialMessage", { batch: chosen.name }),
      });
      setSent(true);
      toast.success(t("attendance.trialSent"));
    } catch (e) {
      toast.error(apiErrorMessage(e, t("errors.generic")));
    } finally {
      setBusy(false);
    }
  };

  if (loaded && slots.length === 0) return null;

  return (
    <div className="space-y-8" data-testid="attendance-section">
      <div>
        <h2 className="font-heading text-lg font-semibold">{t("attendance.batchesTitle")}</h2>
        <div className="mt-3 divide-y divide-border overflow-hidden rounded-2xl border border-border">
          {slots.map((s) => {
            const badge =
              s.capacity == null
                ? t("attendance.onRequest")
                : s.spots_left > 0
                  ? t("attendance.spotsLeft", { count: s.spots_left })
                  : t("attendance.full");
            return (
              <div
                key={s.id}
                data-testid={`attendance-slot-${s.id}`}
                className="flex items-center justify-between gap-3 p-4"
              >
                <div>
                  <p className="font-medium">{s.name}</p>
                  <p className="mt-0.5 tabular-nums text-xs text-muted-foreground">
                    {clockLabel(s.start_time, lang)}–{clockLabel(s.end_time, lang)} ·{" "}
                    {weekdaysLabel(s.weekdays, short)}
                  </p>
                </div>
                <span
                  className={`shrink-0 rounded-full px-3 py-1 text-xs font-semibold ${
                    s.capacity != null && s.spots_left === 0
                      ? "bg-muted text-muted-foreground"
                      : "bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]"
                  }`}
                >
                  {badge}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Storefront S5 (gap review item 8): same dark-header-band booking-card pattern as
          `TableSection` ("Free · no card" instead of "Free · instant" — a trial class needs no
          payment method at all, unlike a table hold) — `Gym Website.dc.html` FREE TRIAL BOOKING. */}
      <div className="overflow-hidden rounded-[22px] border border-border bg-background" data-testid="trial-booking-block">
        <div className="bg-[hsl(var(--brand-deep))] px-6 py-5">
          <div className="flex flex-wrap items-center gap-2.5">
            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-[10px] bg-white/15 text-white">
              <CalendarCheck size={17} />
            </span>
            <h2 className="font-heading text-lg font-bold text-white">{t("attendance.trialTitle")}</h2>
            <span className="ml-auto rounded-full bg-[hsl(var(--accent))] px-3 py-1 text-xs font-bold text-[hsl(var(--accent-ink))]">
              {t("attendance.freeNoCard")}
            </span>
          </div>
          <p className="mt-2.5 max-w-[60ch] text-sm text-white/75">{t("attendance.trialIntro")}</p>
        </div>

        <div className="space-y-4 p-6">
        {sent ? (
          <div className="text-center" data-testid="trial-sent">
            <p className="text-sm text-muted-foreground">{t("attendance.trialSentBody")}</p>
            <Button
              variant="outline"
              className="mt-4 rounded-full"
              data-testid="trial-book-another"
              onClick={() => {
                setSent(false);
                setForm({ name: "", phone: "" });
              }}
            >
              {t("enquiry.sendAnother")}
            </Button>
          </div>
        ) : (
          <>
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                {t("attendance.chooseBatch")}
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {slots.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    data-testid={`trial-batch-${s.id}`}
                    onClick={() => setChosen(s)}
                    className={`rounded-full border px-4 py-1.5 text-sm ${
                      chosen?.id === s.id
                        ? "border-transparent bg-[hsl(var(--primary))] text-primary-foreground"
                        : "border-border"
                    }`}
                  >
                    {s.name}
                  </button>
                ))}
              </div>
            </div>

            {canCapture && (
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="trial-name">{t("enquiry.name")}</Label>
                  <Input
                    id="trial-name"
                    data-testid="trial-name-input"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="trial-phone">{t("enquiry.phone")}</Label>
                  <Input
                    id="trial-phone"
                    data-testid="trial-phone-input"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  />
                </div>
              </div>
            )}

            <Button
              className="w-full rounded-full"
              disabled={busy || !chosen}
              data-testid="trial-book-btn"
              onClick={bookTrial}
            >
              {busy
                ? t("common.saving")
                : chosen
                  ? t("attendance.bookFreeTrial", { batch: chosen.name })
                  : t("attendance.bookFreeTrialGeneric")}
            </Button>
          </>
        )}
        </div>
      </div>
    </div>
  );
}
